/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useCallback } from "react";
import { logger } from "@/utils/logger";
import { motion, AnimatePresence } from "framer-motion";
import { Home, Volume2, VolumeX, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import Confetti from "react-confetti";
import { useWindowSize } from "@/hooks/useWindowSize";
import { recordGameWon } from "@/utils/playerProfile";
import { FLAGS } from "@/utils/quizData";

import { getVoiceLang } from "@/utils/voicePref";
const speakText = (t) => {
  try {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(t);
    u.lang = getVoiceLang(); u.rate = 0.85; u.pitch = 1.0;
    window.speechSynthesis.speak(u);
  } catch (err) { logger.debug("speakText failed", err); }
};

const TebakBendera = ({ onBackToMenu }) => {
  const { width: winW, height: winH } = useWindowSize();
  const [score, setScore] = useState(0);
  const [round, setRound] = useState(0);
  const [target, setTarget] = useState(null);
  const [opts, setOpts] = useState([]);
  const [feedback, setFeedback] = useState(null);
  const [soundOn, setSoundOn] = useState(true);
  const [won, setWon] = useState(false);
  const totalRounds = 12;

  const newQuestion = useCallback(() => {
    const correct = FLAGS[Math.floor(Math.random() * FLAGS.length)];
    const wrongs = [];
    while (wrongs.length < 3) {
      const w = FLAGS[Math.floor(Math.random() * FLAGS.length)];
      if (w.country !== correct.country && !wrongs.find(x => x.country === w.country)) wrongs.push(w);
    }
    const all = [...wrongs, correct].sort(() => Math.random() - 0.5);
    setTarget(correct); setOpts(all); setFeedback(null);
    setTimeout(() => { if (soundOn) speakText(`Which country's flag is this?`); }, 300);
  }, [soundOn]);

  useEffect(() => { newQuestion(); /* eslint-disable-next-line */ }, []);

  const handlePick = (a) => {
    if (feedback) return;
    if (a.country === target.country) {
      setFeedback({ type: "ok", text: `Benar! ${a.country}!` });
      setScore(s => s + 1);
      if (soundOn) speakText(`Correct! ${a.country}!`);
    } else {
      setFeedback({ type: "wrong", text: `Itu ${target.country}` });
      if (soundOn) speakText(`The correct answer is ${target.country}`);
    }
    setTimeout(() => {
      if (round + 1 >= totalRounds) { setWon(true); recordGameWon("bendera"); }
      else { setRound(r => r + 1); newQuestion(); }
    }, 1500);
  };

  if (won) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[80vh] px-4" data-testid="bendera-win">
        <Confetti width={winW} height={winH} recycle={false} numberOfPieces={250} />
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="glass-card-strong rounded-2xl p-8 text-center max-w-md w-full">
          <span className="text-6xl block mb-3">🌍</span>
          <h2 className="font-fredoka text-3xl text-gold font-bold mb-2">Selesai!</h2>
          <p className="font-fredoka text-gold-light/80 mb-4">Skor: {score}/{totalRounds}</p>
          <div className="flex flex-col gap-2">
            <Button onClick={() => { setScore(0); setRound(0); setWon(false); newQuestion(); }}
              className="w-full font-fredoka font-bold rounded-xl h-10 bg-gradient-to-r from-yellow-400 to-amber-500"><RotateCcw className="w-4 h-4 mr-2" />Main Lagi</Button>
            <Button onClick={onBackToMenu} variant="outline" className="w-full glass-card border-gold/20 text-gold font-fredoka rounded-xl">Menu</Button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center w-full max-w-4xl mx-auto px-3 pt-3" data-testid="bendera-game">
      <div className="w-full glass-card-strong rounded-xl p-2 mb-2 flex justify-between items-center">
        <span className="font-fredoka text-gold font-bold text-sm">🌍 {round + 1}/{totalRounds} • Skor: {score}</span>
        <div className="flex gap-1.5">
          <Button onClick={() => setSoundOn(!soundOn)} variant="outline" size="sm" className="glass-card border-gold/20 text-gold h-7 w-7 p-0">{soundOn ? <Volume2 className="w-3 h-3" /> : <VolumeX className="w-3 h-3" />}</Button>
          <Button onClick={onBackToMenu} variant="outline" size="sm" className="glass-card border-gold/20 text-gold h-7 px-2"><Home className="w-3 h-3" /></Button>
        </div>
      </div>

      <motion.div key={round} initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        className="glass-card-strong rounded-2xl p-6 w-full text-center mb-3">
        <p className="font-fredoka text-gold-light/70 text-sm mb-1">Bendera negara apa ini?</p>
        <span className="text-9xl block mb-1 leading-none">{target?.flag}</span>
      </motion.div>

      <AnimatePresence>
        {feedback && (
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ opacity: 0 }} className="mb-3" data-testid={`bendera-feedback-${feedback.type}`}>
            <span className={`font-fredoka font-bold text-base px-4 py-2 rounded-full ${feedback.type === "ok" ? "bg-green-500 text-white" : "bg-red-400 text-white"}`}>
              {feedback.type === "ok" ? "✅" : "❌"} {feedback.text}
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid grid-cols-2 gap-4 w-full">
        {opts.map((a, i) => (
          <motion.button key={`${a.country}-${i}`} data-testid={`bendera-opt-${i}`}
            onClick={() => handlePick(a)} disabled={!!feedback}
            whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.95 }}
            className="ingredient-3d quiz-option-btn text-center text-slate-800 font-fredoka font-bold">
            {a.country}
          </motion.button>
        ))}
      </div>
    </div>
  );
};

export default TebakBendera;
